import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { updateNotificationSettings as updateNotificationSettingsRpc } from '../services/notificationService'
import { useUser } from './UserContext'

const AppNotificationsContext = createContext(null)

// Polling interval for checking new notifications (30 seconds)
const POLL_INTERVAL = 30000

export function AppNotificationsProvider({ children }) {
  const { user } = useUser()
  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [settings, setSettings] = useState(null)

  // Fetch notifications from the server
  const fetchNotifications = useCallback(async (limit = 20) => {
    if (!user?.id) return

    setLoading(true)
    try {
      const { data, error } = await supabase.rpc('get_notifications', {
        p_limit: limit,
        p_offset: 0,
        p_unread_only: false
      })

      if (error) throw error
      setNotifications(data || [])
    } catch (err) {
      console.error('Error fetching notifications:', err)
    } finally {
      setLoading(false)
    }
  }, [user?.id])

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    if (!user?.id) return

    try {
      const { data, error } = await supabase.rpc('get_unread_notification_count')
      if (error) throw error
      setUnreadCount(data || 0)
    } catch (err) {
      console.error('Error fetching unread count:', err)
    }
  }, [user?.id])

  // Fetch notification settings
  const fetchSettings = useCallback(async () => {
    if (!user?.id) return

    try {
      const { data, error } = await supabase.rpc('get_notification_settings')
      if (error) throw error
      setSettings(data)
    } catch (err) {
      console.error('Error fetching notification settings:', err)
    }
  }, [user?.id])

  // Mark a single notification as read
  const markAsRead = useCallback(async (notificationId) => {
    try {
      const { error } = await supabase.rpc('mark_notification_read', {
        p_notification_id: notificationId
      })

      if (error) throw error

      // Update local state
      setNotifications(prev =>
        prev.map(n =>
          n.id === notificationId
            ? { ...n, is_read: true, read_at: new Date().toISOString() }
            : n
        )
      )
      setUnreadCount(prev => Math.max(0, prev - 1))

      return true
    } catch (err) {
      console.error('Error marking notification as read:', err)
      return false
    }
  }, [])

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      const { error } = await supabase.rpc('mark_all_notifications_read')

      if (error) throw error

      // Update local state
      setNotifications(prev =>
        prev.map(n => ({ ...n, is_read: true, read_at: new Date().toISOString() }))
      )
      setUnreadCount(0)

      return true
    } catch (err) {
      console.error('Error marking all notifications as read:', err)
      return false
    }
  }, [])

  // Update notification settings — forwards every known key to the RPC via the service wrapper.
  const updateSettings = useCallback(async (newSettings) => {
    try {
      const data = await updateNotificationSettingsRpc(newSettings)
      setSettings(data)
      return true
    } catch (err) {
      console.error('Error updating notification settings:', err)
      return false
    }
  }, [])

  // Get the navigation path for a notification
  const getNotificationPath = useCallback((notification) => {
    const { type, data } = notification

    switch (type) {
      case 'bracket_official_updated':
        // Deep-link directly to the bracket picks view so we can auto-jump to the updated round.
        if (data?.bracket_id) {
          return `/bracket/${data.bracket_id}/picks?from=notification`
        }
        return '/tournaments'

      case 'prize_awarded':
        return '/prizes'

      case 'group_invite':
        if (data?.group_id) {
          return `/groups/${data.group_id}`
        }
        return '/groups'

      case 'new_tournament':
        if (data?.tournament_id) {
          return `/tournaments/${data.tournament_id}`
        }
        return '/tournaments'

      case 'picks_open':
      case 'lock_reminder':
        if (data?.bracket_id) {
          return `/bracket/${data.bracket_id}/picks`
        }
        if (data?.tournament_id) {
          return `/tournaments/${data.tournament_id}`
        }
        return '/tournaments'

      case 'streak_milestone':
        if (data?.bracket_id) {
          return `/bracket/${data.bracket_id}/picks?highlight=streak`
        }
        return '/profile'

      // Messages and kudos open the group's Messages tab, where they are kept.
      case 'group_message':
      case 'group_kudos':
        if (data?.group_id) {
          return `/groups/${data.group_id}/messages`
        }
        return '/groups'

      case 'group_quiet':
        if (data?.group_id) {
          return `/groups/${data.group_id}`
        }
        return '/groups'

      default:
        return '/profile'
    }
  }, [])

  // Initial fetch when user logs in
  useEffect(() => {
    if (user?.id) {
      fetchUnreadCount()
      fetchSettings()
    } else {
      // Reset state when user logs out
      setNotifications([])
      setUnreadCount(0)
      setSettings(null)
    }
  }, [user?.id, fetchUnreadCount, fetchSettings])

  // Poll for new notifications
  useEffect(() => {
    if (!user?.id) return

    const interval = setInterval(() => {
      fetchUnreadCount()
    }, POLL_INTERVAL)

    return () => clearInterval(interval)
  }, [user?.id, fetchUnreadCount])

  const value = {
    notifications,
    unreadCount,
    loading,
    settings,
    fetchNotifications,
    fetchUnreadCount,
    fetchSettings,
    markAsRead,
    markAllAsRead,
    updateSettings,
    getNotificationPath
  }

  return (
    <AppNotificationsContext.Provider value={value}>
      {children}
    </AppNotificationsContext.Provider>
  )
}

export function useAppNotifications() {
  const context = useContext(AppNotificationsContext)
  if (!context) {
    throw new Error('useAppNotifications must be used within an AppNotificationsProvider')
  }
  return context
}
